import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  BadgeOutlined,
  CalendarMonthOutlined,
  CategoryOutlined,
  EditOutlined,
  EventRepeatOutlined,
  MoreTimeOutlined,
  ApartmentOutlined,
  ScheduleOutlined,
  TaskAltOutlined,
    StorageOutlined,
} from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestDetailHeader } from "components/DesignSystem/RequestDetailHeader";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { ViewActionFooter } from "../Shared/ViewActionFooter";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter } from "components/CustomComponents/DesignSystemButtons";
import { PayInputActivityLog } from "../Shared/PayInputActivityLog";
import { DateField, SelectField, TextField, TimeField, toOptions } from "../Shared/fieldInputs";
import { Field, FormSection, panelBodySx } from "../Shared/formUi";
import { formatDate, formatExactQty, formatMoney } from "../Shared/formatters";
import { tooltipStyleProps } from "services/constants/Styles";
import {
  designColors,
  designShell,
} from "services/constants/designTokens";
import { formatRawQuantity, isAmountBasis, isDuration, numericQuantity, validateOvertime } from "./overtimeApi";
import { loadShiftFormOptions } from "../ShiftEarnings/shiftEarningsApi";
import { useSalaryRateAutofill } from "../Shared/useSalaryRateAutofill";
import { useShiftSchedules } from "../ShiftEarnings/useShiftSchedules";
import { CustomAutoCompleteWithLogo } from "components/CustomComponents/CustomAutoCompleteWithLogo";
import { customGetAPIQuiet } from "services/utils/CustomAPI";
import { SelectedEmployeeHeader } from "pages/TeamZone/Approve/SelectedEmployeeHeader";

// Label with the red required asterisk (same as Shift Earnings).
const required = (label) => (
  <>
    {label} <Mui.Box component="span" sx={{ color: "#DC2626" }}>*</Mui.Box>
  </>
);

// source: the entry's source when editing (shown read-only). Attendance
// entries come from attendance, so only their Payroll Cycle can be changed
// (same rule as Shift Earnings).
// The two built-in bands are left out of the pick list (same as the Shift
// Category band screen); an empty OT Band uses the default band.
const isDefaultBand = (name) => ["attendance", "shift earning"].includes(String(name ?? "").trim().toLowerCase());

// showApproval: false when Pay Input Configuration has no approval step.
// showBand: false when bands are off (compliance/ is_band_allowed): the OT
// Band field is hidden and the entry keeps whatever band it already has.
export const OvertimeFormPanel = ({ open, mode, initial, source, showApproval = true, showBand = true, onClose, onSave, expanded, onToggleExpand, busy }) => {
  const { domain, topbarCompanyId } = useSelector((state) => state.tokenReducer);
  const history = useHistory();
  const locked = mode === "edit" && source === "Attendance";
  const [form, setForm] = useState(initial);
  const [showErrors, setShowErrors] = useState(false);
  const [options, setOptions] = useState({ employees: [], shifts: [], errors: [] });
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => { if (open) { setForm(initial); setShowErrors(false); } }, [open, initial]);
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    setOptionsLoading(true); setOptions({ employees: [], shifts: [], errors: [] });
    loadShiftFormOptions(domain, topbarCompanyId, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setOptions(data); })
      .catch((error) => { if (!controller.signal.aborted) setOptions({ employees: [], shifts: [], errors: [error.message] }); })
      .finally(() => { if (!controller.signal.aborted) setOptionsLoading(false); });
    return () => controller.abort();
  }, [open, domain, topbarCompanyId, retry]);
  // OT band names for the OT Band pick list (Shift Category > Overtime Bands).
  const [bands, setBands] = useState({ names: [], loading: false, error: "" });
  useEffect(() => {
    if (!open || !showBand || !domain) return undefined;
    let cancelled = false;
    setBands({ names: [], loading: true, error: "" });
    customGetAPIQuiet(`${domain}administration/shiftroster/shiftrota/overtime-bands/`).then((response) => {
      if (cancelled || !response) return;
      const list = Array.isArray(response.responseDetails) ? response.responseDetails : [];
      setBands(response.ok
        ? { names: list.map((band) => String(band?.band_name ?? "").trim()).filter((name) => name && !isDefaultBand(name)), loading: false, error: "" }
        : { names: [], loading: false, error: "Couldn't load OT bands." });
    });
    return () => { cancelled = true; };
  }, [open, showBand, domain, retry]);
  const { schedules, loading: schedulesLoading, error: scheduleError, retry: retrySchedules } = useShiftSchedules(domain, [form?.employee_code], open);
  const errors = useMemo(() => form ? validateOvertime(form, mode === "edit" ? initial : null) : {}, [form, initial, mode]);

  // Rate auto-fill, same rule as Shift Earnings: on Add, and on Edit of
  // Manual / Import entries. Attendance-sourced entries keep their rate.
  const employeeUserId = options.employees.find((option) => option.value === form?.employee_code)?.user_id || "";
  useSalaryRateAutofill({
    domain,
    enabled: open && !locked,
    userId: employeeUserId,
    payScheduleId: form?.pay_schedule_id,
    date: form?.date,
    basis: form?.basis,
    setForm,
  });

  if (!form) return null;
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value, ...(key === "employee_code" ? { pay_schedule_id: "" } : {}) }));
  const error = (key) => showErrors ? errors[key] : undefined;
  // Shaped for CustomAutoCompleteWithLogo (photo, name, code), like Shift Earnings.
  const employeeOptions = [...options.employees];
  if (form.employee_code && !employeeOptions.some((item) => item.value === form.employee_code)) employeeOptions.push({ value: form.employee_code, id: form.employee_code, employee_id: form.employee_code, label: initial?.employee_name || form.employee_code, image: initial?.image_url || "", image_url: initial?.image_url || "" });
  const selectedEmployee = employeeOptions.find((item) => item.value === form.employee_code) ?? null;
  const cycleOptions = [...schedules];
  if (mode === "edit" && initial?.pay_schedule_id && !cycleOptions.some((item) => item.value === initial.pay_schedule_id)) cycleOptions.push({ value: initial.pay_schedule_id, label: initial.payroll_cycle || initial.pay_schedule_id });
  // Only the backend's bands; the field starts empty (no made-up option).
  const bandOptions = toOptions([...new Set(bands.names)]);
  if (form.band_name && !bandOptions.some((item) => item.value === form.band_name)) bandOptions.push({ value: form.band_name, label: form.band_name });
  const shiftOptions = [...options.shifts];
  if (form.shift_name && !shiftOptions.some((item) => item.value === form.shift_name)) shiftOptions.push({ value: form.shift_name, label: form.shift_name });
  const input = (key, type) => <TextField value={form[key]} type={type} disabled={locked || busy} error={error(key)} onChange={set(key)} />;
  const previewQty = numericQuantity(form.quantity);
  const previewAmount = previewQty != null && String(form.rate).trim() && String(form.payment_factor).trim() ? previewQty * Number(form.rate) * Number(form.payment_factor) : null;
  return (
    <GlobalSidePanel open={open} onClose={busy ? undefined : onClose} expanded={expanded} onToggleExpand={onToggleExpand}
      title={mode === "edit" ? "Edit Overtime" : "Add Overtime"}
      subtitle={locked ? "This entry comes from ERP (Attendance). Only the payroll cycle can be changed." : `Manage the overtime quantity, rate${showBand ? ", band" : ""} and payroll cycle.`}
      // Payroll -> Payroll home; Overtime -> back to the list. Not while saving.
      breadcrumb={[
        { label: "Payroll", onClick: () => { if (!busy) history.push("/admin/payroll/setup"); } },
        "Pay Inputs",
        { label: "Overtime", onClick: () => { if (!busy) onClose(); } },
        mode === "edit" ? "Edit Overtime" : "Add Overtime",
      ]}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel={busy ? "Saving..." : mode === "edit" ? "Save Changes" : "Add"} primaryProps={{ disabled: busy || optionsLoading, onClick: () => { setShowErrors(true); if (!Object.keys(errors).length) onSave(form); } }} />}
    >
      <Mui.Stack spacing={2}>
        {options.errors.length ? <Mui.Alert severity="warning" action={<Mui.Button onClick={() => setRetry((n) => n + 1)}>Retry</Mui.Button>}>{options.errors.join(" ")}</Mui.Alert> : null}
        {bands.error ? <Mui.Alert severity="warning" action={<Mui.Button onClick={() => setRetry((n) => n + 1)}>Retry</Mui.Button>}>{bands.error}</Mui.Alert> : null}
        {scheduleError ? <Mui.Alert severity="warning" action={<Mui.Button onClick={retrySchedules}>Retry</Mui.Button>}>{scheduleError}</Mui.Alert> : null}
        {locked ? (
          <RequestInfoBanner
            title="Overtime details (read only)"
            body="This record is created from ERP (Attendance). Only the payroll cycle can be changed."
          />
        ) : null}
        {/* Same layout as the Shift Earnings form: every field has a label
            and a one-line info helper (so paired inputs line up), required
            fields carry a red *, read-only values sit in disabled inputs. */}
        <FormSection title="Employee" info="Select the employee this overtime entry is for.">
          <Mui.Box sx={{
            gridColumn: "1 / -1",
            ...(mode === "edit" ? {
              "& .MuiInputBase-input.Mui-disabled": {
                color: designColors.slate900,
                WebkitTextFillColor: designColors.slate900,
                opacity: 1,
              },
            } : {}),
          }}>
            <Field label={required("Employee")} helper="Employee who worked the overtime. Search by name or employee code." error={error("employee_code")}>
              <CustomAutoCompleteWithLogo
                dataList={employeeOptions}
                selectedValue={selectedEmployee}
                updateDataFunction={(event, option) => set("employee_code")(option?.value ?? "")}
                placeHolder={optionsLoading ? "Loading employees..." : "Select employee"}
                textBoxError={Boolean(error("employee_code"))}
                disableAction={mode === "edit" || busy || optionsLoading}
                showAvathar={!selectedEmployee}
              />
            </Field>
          </Mui.Box>
        </FormSection>
        {/* Picked employee's card on its own, same as Shift Earnings. */}
        {selectedEmployee ? (
          <SelectedEmployeeHeader
            selectedUser={{
              ...selectedEmployee,
              department_name: selectedEmployee.department_name || initial?.department_name,
              designation: selectedEmployee.designation || initial?.designation,
            }}
          />
        ) : null}

        <FormSection title="Payroll Details" info="Payroll cycle this entry is paid in.">
          <Field label="Payroll Cycle" helper="Blank = employee's own schedule.">
            <SelectField
              value={form.pay_schedule_id}
              onChange={set("pay_schedule_id")}
              options={[{ value: "", label: mode === "edit" ? "Unassigned" : "Automatic" }, ...cycleOptions]}
              disabled={busy || schedulesLoading || !form.employee_code}
              placeholder={schedulesLoading ? "Loading payroll cycles..." : "Select payroll cycle"}
            />
          </Field>
          <Field label="Source" helper="Where this entry came from.">
            <TextField value={mode === "edit" ? (locked ? "Attendance (ERP)" : source || "–") : "Manual"} disabled onChange={() => {}} />
          </Field>
        </FormSection>

        <FormSection title="Overtime Details" info={showBand ? "Select the date, shift and OT band (optional)." : "Select the date and shift."}>
          {/* The date can't be changed once saved (the server refuses it). */}
          <Field label={required("Date")} helper={mode === "edit" ? "Can't be changed once saved." : "Date the overtime was worked."} error={error("date")}>
            <DateField value={form.date} disabled={busy || mode === "edit"} error={error("date")} onChange={set("date")} />
          </Field>
          <Field label="Shift" helper="Shift worked in (optional).">
            <SelectField value={form.shift_name} onChange={set("shift_name")} options={shiftOptions} disabled={locked || busy || optionsLoading} placeholder="Select shift" />
          </Field>
          {showBand ? (
            <Field label="OT Band" helper="Optional. Leave empty if no band applies.">
              <SelectField
                value={form.band_name}
                onChange={set("band_name")}
                options={bandOptions}
                disabled={locked || busy || bands.loading}
                placeholder={bands.loading ? "Loading OT bands..." : "Select OT band"}
              />
            </Field>
          ) : null}
          <Field label={required("Basis")} helper="Pay per shift or per hour." error={error("basis")}>
            {/* Switching basis clears Credited Quantity: an HH:MM time is not a
                number of shifts (and the reverse), so it is entered again in
                the new format. */}
            <SelectField
              value={form.basis}
              onChange={(basis) => setForm((current) => (basis === current.basis ? current : { ...current, basis, quantity: "" }))}
              options={[{ value: "shift", label: "Shift" }, { value: "hours", label: "Hours" }]}
              disabled={locked || busy}
              error={error("basis")}
              placeholder="Select basis"
            />
          </Field>
          {/* Hours basis: 24-hour clock picker (HH:MM), same as the other
              duration fields; Shift basis: number of shifts. Full width when
              OT Band is shown (Basis pairs with it); otherwise it sits next
              to Basis. */}
          <Field wide={showBand} label={required("Credited Quantity")} helper={form.basis === "hours" ? "Overtime hours worked, as HH:MM." : "Number of shifts credited."} error={error("quantity")}>
            {form.basis === "hours" ? (
              <TimeField value={form.quantity} onChange={set("quantity")} error={error("quantity")} disabled={locked || busy} />
            ) : (
              input("quantity", "number")
            )}
          </Field>
        </FormSection>

        <FormSection title="Rate & Payroll" info="The server calculates and saves the final amount.">
          <Field label={required("Rate (₹)")} helper="Paid per shift or per hour." error={error("rate")}>{input("rate", "number")}</Field>
          <Field label="Factor" helper={showBand ? "Blank = the band's factor." : "Blank = the default factor."} error={error("payment_factor")}>{input("payment_factor", "number")}</Field>
          {mode === "edit" && showApproval ? (
            <Field label="Status" helper="Approval status of this entry.">
              <SelectField value={form.status} onChange={set("status")} disabled={locked || busy} options={toOptions(["Pending", "Approved", "Rejected"])} />
            </Field>
          ) : null}
          <Field wide={!(mode === "edit" && showApproval)} label="Estimated Payable (₹)" helper="Quantity × rate × factor.">
            <TextField value={previewAmount == null ? "Calculated on save" : formatMoney(previewAmount)} disabled onChange={() => {}} />
          </Field>
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};

const viewMoney = (value) => (value == null || value === "" ? "–" : formatMoney(value));
const toNumber = (value) => (value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value));

// The View panel's calculation line, matching the list columns (Overtime has
// no Penalty / Net / Payable Qty columns):
//   Credited Qty × Factor × Rate = Earning Amount.
// The amount is the server's, never recalculated here. Hours basis
// quantities are decimal hours (02:30 -> 2.50 hrs).
export const overtimeCalculation = (row) => ({
  credited: toNumber(row.quantity),
  factor: toNumber(row.payment_factor) ?? 1,
  amount: row.earning_amount ?? row.amount ?? row.payable_amount,
  unit: row.basis === "hours" ? " hrs" : "",
});

export const OvertimeViewPanel = ({
  open,
  row,
  onClose,
  onEdit,
  onDelete,
  // (row, "Approved" | "Rejected"): opens the list's confirm dialog.
  onStatus,
  busy = false,
  canEdit,
  // false when Pay Input Configuration has no approval step: Status is left out.
  showApproval = true,
  // false when bands are off (compliance/ is_band_allowed): OT Band is left out.
  showBand = true,
  expanded,
  onToggleExpand,
}) => {
  const { domain } = useSelector((state) => state.tokenReducer);
  const history = useHistory();
  const [tab, setTab] = useState("info");
  // Each record opens on its Information tab.
  useEffect(() => setTab("info"), [row?.id]);

  if (!row) return null;
  const { indigo, blue, green, orange, purple, pink } = detailAccentPalette;
  const calc = overtimeCalculation(row);
  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      expanded={expanded}
      onToggleExpand={onToggleExpand}
      title="Overtime Details"
      subtitle="View overtime information and calculation details."
      // Payroll -> Payroll home; Overtime -> back to the list (closes this panel).
      breadcrumb={[
        { label: "Payroll", onClick: () => history.push("/admin/payroll/setup") },
        "Pay Inputs",
        { label: "Overtime", onClick: onClose },
        "Overtime Details",
      ]}
      bodySx={panelBodySx}
      headerActions={
        canEdit ? (
          <Mui.Tooltip title="Edit" arrow slotProps={tooltipStyleProps}>
            <Mui.IconButton
              size="small"
              aria-label="Edit"
              onClick={() => onEdit(row)}
              sx={{ ...designShell.panelSoftIconButton, color: designColors.tealDark }}
            >
              <EditOutlined fontSize="small" />
            </Mui.IconButton>
          </Mui.Tooltip>
        ) : null
      }
      // Delete sits in the footer as a full-width button, like the Cancel
      // action on Team Workspace Approvals' detail panels.
      footer={
        canEdit ? (
          <ViewActionFooter
            approval={showApproval ? { status: row.status, disabled: busy, onApprove: onStatus && (() => onStatus(row, "Approved")), onReject: onStatus && (() => onStatus(row, "Rejected")) } : null}
            recordAction={{ label: "Delete Overtime", onClick: () => onDelete(row), disabled: busy }}
          />
        ) : null
      }
    >
      {/* Employee card, same as the Shift Earnings view. */}
      <Mui.Box sx={{ mb: 2 }}>
        <RequestDetailHeader
          userDetails={{
            image_url: row.image_url,
            user_name: row.employee_name,
            employee_id: row.employee_code,
            department_name: row.department_name || row.department || "N/A",
            designation: row.designation_name || row.designation,
          }}
          status={showApproval ? row.status : undefined}
        />
      </Mui.Box>
      <Mui.Tabs
        value={tab}
        onChange={(event, value) => setTab(value)}
        variant="fullWidth"
        sx={{
          ...designShell.panelTabs,
          mt: 0,
          mb: 2,
          borderBottom: `1px solid ${designColors.slate200}`,
        }}
      >
        <Mui.Tab label="Information" value="info" />
        <Mui.Tab label="Activity Log" value="audit" />
      </Mui.Tabs>
      {tab === "audit" ? (
        <PayInputActivityLog domain={domain} type="OVERTIME" recordId={row.id} refreshKey={row.modifiedat} />
      ) : (
        // Same three sections as the Shift Earnings view: details, the
        // step-by-step calculation, then payroll.
        <Mui.Stack spacing={2}>
          <RequestDetailsSection
            title="OVERTIME DETAILS"
            fields={[
              { icon: CalendarMonthOutlined, label: "Date", value: formatDate(row.date), accent: indigo },
              ...(showBand ? [{ icon: CategoryOutlined, label: "OT Band", value: row.band_name || "–", accent: pink }] : []),
              { icon: ScheduleOutlined, label: "Shift", value: row.shift_name || "–", accent: blue },
              // The OT hours worked (ot_working_hours, the list's Working Hrs).
              // final_approved_ot is the credited quantity, already shown
              // under Calculation. "HH:MM" is shown as sent, a number as "2.50 hrs".
              {
                icon: MoreTimeOutlined,
                label: "OT Hours",
                value: row.worked_hours == null ? "–" : `${formatRawQuantity(row.ot_working_hours, row.worked_hours)}${isDuration(row.ot_working_hours) ? "" : " hrs"}`,
                accent: orange,
              },
              { icon: BadgeOutlined, label: "Basis", value: row.basis || "–", accent: purple },
            ]}
          />
          <RequestDetailsSection
            title="CALCULATION"
            fields={[
              {
                icon: StorageOutlined,
                label: "Credited Qty",
                value: calc.credited == null ? "–" : `${formatRawQuantity(row.raw_quantity, calc.credited)}${isDuration(row.raw_quantity) ? "" : calc.unit}`,
                accent: green,
              },
              {
                icon: StorageOutlined,
                label: "Credited Qty × Factor × Rate = Earning Amount",
                value: `${calc.credited == null ? "–" : formatRawQuantity(row.raw_quantity, calc.credited)} ×${formatExactQty(calc.factor)} × ${isAmountBasis(row) ? "–" : viewMoney(row.rate)} = ${viewMoney(calc.amount)}`,
                accent: orange,
              },
              ...(row.gross_pay != null ? [{ icon: StorageOutlined, label: "Gross Pay", value: viewMoney(row.gross_pay), accent: orange }] : []),
              ...(row.net_pay != null ? [{ icon: StorageOutlined, label: "Net Pay", value: viewMoney(row.net_pay), accent: orange }] : []),
            ]}
          />
          <RequestDetailsSection
            title="PAYROLL"
            fields={[
              ...(row.company_name ? [{ icon: ApartmentOutlined, label: "Company", value: row.company_name, accent: indigo }] : []),
              { icon: EventRepeatOutlined, label: "Payroll Cycle", value: row.payroll_cycle || "–", accent: indigo },
              ...(showApproval ? [{ icon: TaskAltOutlined, label: "Approval Status", value: row.status || "–", accent: blue }] : []),
              { icon: StorageOutlined, label: "Source", value: row.source || "–", accent: purple },
            ]}
          />
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
